import { Slot, type Slottable } from "@suse-o11y/slot";
import { useMemo } from "@suse-o11y/use-memo";
import React from "react";
// biome-ignore lint/style/noRestrictedImports: component usage
import type { DraggableProps as BeautifulDraggableProps } from "react-beautiful-dnd";

import { DraggableContextProvider } from "./context/draggable";
import { DraggableContent } from "./_draggable-content";
import { useIsEnabled } from "./_enabler";

const LazyDraggable = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Draggable } = await import("react-beautiful-dnd");

	return { default: Draggable };
});

export interface DraggableProps
	extends Slottable,
		React.HTMLAttributes<HTMLElement>,
		Omit<BeautifulDraggableProps, "draggableId" | "children"> {
	draggableId?: string;
	children: React.ReactNode;
}

export const Draggable = React.forwardRef<React.ComponentRef<typeof LazyDraggable>, DraggableProps>(
	(
		{
			asChild = false,
			draggableId,
			index,
			isDragDisabled,
			disableInteractiveElementBlocking,
			shouldRespectForcePress,
			children,
			...props
		},
		ref,
	) => {
		const Root = asChild ? Slot : "div";
		const draggableIdFallback = React.useId();
		const draggableIdWithFallback = draggableId ?? draggableIdFallback;
		const isEnabled = useIsEnabled();

		const fallbackDraggableContext = useMemo(
			() => ({
				dragHandleProps: undefined,
				value: {
					isDragging: false,
					isDropAnimating: false,
					draggableId: draggableIdWithFallback,
					source: { droppableId: "", index },
				},
			}),
			[draggableIdWithFallback, index],
		);

		if (!isEnabled) {
			return (
				<DraggableContextProvider value={fallbackDraggableContext}>
					<Root {...props}>{children}</Root>
				</DraggableContextProvider>
			);
		}

		return (
			<React.Suspense
				fallback={
					<DraggableContextProvider value={fallbackDraggableContext}>
						<Root {...props}>{children}</Root>
					</DraggableContextProvider>
				}
			>
				<LazyDraggable
					disableInteractiveElementBlocking={disableInteractiveElementBlocking}
					draggableId={draggableIdWithFallback}
					index={index}
					isDragDisabled={isDragDisabled}
					ref={ref}
					shouldRespectForcePress={shouldRespectForcePress}
				>
					{({ innerRef, draggableProps, dragHandleProps }, snapshot, rubric) => (
						<DraggableContent
							data-dragging={snapshot.isDragging || undefined}
							dragHandleProps={dragHandleProps}
							ref={innerRef}
							rubric={rubric}
							snapshot={snapshot}
							{...draggableProps}
						>
							<Root {...props}>{children}</Root>
						</DraggableContent>
					)}
				</LazyDraggable>
			</React.Suspense>
		);
	},
);
